import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import Table from "../../../shared/components/common/Table";
import SearchBar from "../../../shared/components/common/SearchBar";
import TableToolbar from "../../../shared/components/common/TableToolbar";
import StatusBadge from "../../../shared/components/common/StatusBadge";
import Tooltip from "../../../shared/components/common/Tooltip";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import UserManagementHeader from "./UserManagementHeader";
import ActionButtons from "../../../shared/components/common/ActionButtons";
import EditUserModal from "./EditUserModal";
import AddUserModal from "./AddUserModal";
import DeleteUserModal from "./DeleteUserModal";
import BulkImportModal from "./BulkImportModal";
import SelectionToolbar from "../../../shared/components/common/SelectionToolbar";
import { useForm } from "../../../shared/hooks/useForm";
import useUsers from "../hooks/useUsers";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { getPhoneNumbersByClientId } from "../../../core/api/clientsService";
import {
  getPhoneNumberById,
  updatePhoneNumberAssociation,
} from "../../numbers/services/numbersService";
import "../../logs/Logs.css";

const UserManagement = () => {
  const {
    users,
    isLoading,
    isProcessing,
    fetchUsers,
    handleAddUser: apiAddUser,
    handleUpdateUser: apiUpdateUser,
    handleDeleteUser: apiDeleteUser,
    handleBulkDelete: apiBulkDelete,
    handleBulkStatusChange: apiBulkStatusChange,
  } = useUsers();

  const currentUser = useSelector(selectCurrentUser);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [deliveryAddressFilter, setDeliveryAddressFilter] = useState("");
  const [emailModeFilter, setEmailModeFilter] = useState("all");
  const [smsModeFilter, setSmsModeFilter] = useState("all");
  const [spreadTable, setSpreadTable] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addErrorMessage, setAddErrorMessage] = useState("");
  const [editErrorMessage, setEditErrorMessage] = useState("");
  const [pendingUserId, setPendingUserId] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);

  const currentUserEmail = useMemo(
    () => String(currentUser?.email || "").trim().toLowerCase(),
    [currentUser?.email],
  );
  const resolvedClientId = useMemo(
    () => Number(currentUser?.client_id || 0) || null,
    [currentUser?.client_id],
  );

  const [editFormState, editInputHandler, editSelectHandler, setEditFormData] =
    useForm(
      {
        firstName: { value: "", isValid: false },
        lastName: { value: "", isValid: false },
        userStatus: { value: "Enabled", isValid: true },
        smsMode: { value: "Disabled", isValid: true },
        mobileNumber: { value: "", isValid: true },
        voicemailNumberId: { value: "", isValid: true },
        timeZone: { value: "UTC", isValid: true },
      },
      false,
    );

  const [addFormState, addInputHandler, addSelectHandler, setAddFormData] =
    useForm(
      {
        firstName: { value: "", isValid: false },
        lastName: { value: "", isValid: false },
        deliveryAddress: { value: "", isValid: false },
        password: { value: "", isValid: false },
        emailMode: { value: "Enabled", isValid: true },
        smsMode: { value: "Disabled", isValid: true },
        mobileNumber: { value: "", isValid: true },
        voicemailNumberId: { value: "", isValid: true },
        timeZone: { value: "UTC", isValid: true },
        userRole: { value: "viewer", isValid: true },
      },
      false,
    );

  const loadPhoneNumbers = useCallback(async () => {
    if (!resolvedClientId) {
      setPhoneNumbers([]);
      return;
    }

    const response = await getPhoneNumbersByClientId(resolvedClientId, {
      limit: 100,
      offset: 0,
    });

    if (response?.error) {
      setPhoneNumbers([]);
      return;
    }

    setPhoneNumbers(Array.isArray(response?.phoneNumbers) ? response.phoneNumbers : []);
  }, [resolvedClientId]);

  useEffect(() => {
    loadPhoneNumbers();
  }, [loadPhoneNumbers]);

  const phoneNumberLookup = useMemo(() => {
    return new Map(phoneNumbers.map((record) => [Number(record.id), record]));
  }, [phoneNumbers]);

  const resolveVoicemailLabel = useCallback((record) => {
    if (!record) return "";
    const phoneValue = record.phone_number || record.phoneNumber || "";
    const friendlyName = record.friendly_name || record.friendlyName || "";
    if (friendlyName && friendlyName !== phoneValue) {
      return `${friendlyName} (${phoneValue})`;
    }
    return phoneValue;
  }, []);

  const assignedVoicemailIds = useMemo(() => {
    const ids = new Set();
    users.forEach((user) => {
      const rawId = Number(user.voicemailNumberId ?? user.voicemail_number_id);
      if (Number.isFinite(rawId) && rawId > 0) {
        ids.add(rawId);
      }
    });
    return ids;
  }, [users]);

  const usersWithVoicemailNumbers = useMemo(() => {
    return users.map((user) => {
      const rawId = Number(user.voicemailNumberId ?? user.voicemail_number_id);
      const voicemailNumberId =
        Number.isFinite(rawId) && rawId > 0 ? rawId : null;
      const record = voicemailNumberId
        ? phoneNumberLookup.get(Number(voicemailNumberId))
        : null;
      const voicemailNumber = record?.phone_number || record?.phoneNumber || "";

      return {
        ...user,
        voicemailNumberId,
        voicemailNumber,
      };
    });
  }, [users, phoneNumberLookup]);

  const availableVoicemailNumbers = useMemo(() => {
    return phoneNumbers.filter((record) => {
      const recordId = Number(record?.id);
      if (Number.isFinite(recordId) && assignedVoicemailIds.has(recordId)) {
        return false;
      }
      return !record?.is_associated;
    });
  }, [phoneNumbers, assignedVoicemailIds]);

  const hydrateMissingVoicemailNumbers = useCallback(async () => {
    const missingIds = Array.from(assignedVoicemailIds).filter(
      (id) => !phoneNumberLookup.has(id),
    );

    if (missingIds.length === 0) {
      return;
    }

    const responses = await Promise.all(
      missingIds.map((id) => getPhoneNumberById(id)),
    );

    const fetched = responses
        .filter((response) => response && !response.error)
        .map((response) => {
          if (Array.isArray(response?.phone_numbers)) {
            return response.phone_numbers[0];
          }
          return response?.data || response;
        })
        .filter((record) => record && record.id);

    if (fetched.length === 0) {
      return;
    }

    setPhoneNumbers((current) => {
      const existing = new Map(current.map((record) => [Number(record.id), record]));
      fetched.forEach((record) => {
        existing.set(Number(record.id), record);
      });
      return Array.from(existing.values());
    });
  }, [assignedVoicemailIds, phoneNumberLookup]);

  useEffect(() => {
    hydrateMissingVoicemailNumbers();
  }, [hydrateMissingVoicemailNumbers]);

  useEffect(() => {
    const voicemailId = Number(selectedUser?.voicemailNumberId);
    if (!Number.isFinite(voicemailId) || voicemailId <= 0) {
      return;
    }

    if (phoneNumberLookup.has(voicemailId)) {
      return;
    }

    getPhoneNumberById(voicemailId).then((response) => {
      if (!response || response.error) return;
      const record = response?.data || response;
      if (!record || !record.id) return;

      setPhoneNumbers((current) => {
        const existing = new Map(
          current.map((item) => [Number(item.id), item]),
        );
        existing.set(Number(record.id), record);
        return Array.from(existing.values());
      });
    });
  }, [selectedUser, phoneNumberLookup, getPhoneNumberById]);

  const editVoicemailNumbers = useMemo(() => {
    if (!selectedUser?.voicemailNumberId) {
      return availableVoicemailNumbers;
    }

    return phoneNumbers.filter((record) => {
      const recordId = Number(record?.id);
      if (Number(recordId) === Number(selectedUser.voicemailNumberId)) {
        return true;
      }

      if (Number.isFinite(recordId) && assignedVoicemailIds.has(recordId)) {
        return false;
      }

      return !record?.is_associated;
    });
  }, [availableVoicemailNumbers, phoneNumbers, selectedUser, assignedVoicemailIds]);

  const selectedVoicemailLabel = useMemo(() => {
    if (!selectedUser?.voicemailNumberId) return "";
    const record = phoneNumberLookup.get(Number(selectedUser.voicemailNumberId));
    return resolveVoicemailLabel(record) || selectedUser.voicemailNumber || "";
  }, [selectedUser, phoneNumberLookup, resolveVoicemailLabel]);

  const filteredUsers = useMemo(() => {
    let filtered = usersWithVoicemailNumbers;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.firstName.toLowerCase().includes(searchLower) ||
          user.lastName.toLowerCase().includes(searchLower) ||
          user.toAddress.toLowerCase().includes(searchLower) ||
          user.deliveryAddress.toLowerCase().includes(searchLower) ||
          user.mobileNumber.includes(searchTerm) ||
          (user.voicemailNumber && user.voicemailNumber.includes(searchTerm)),
      );
    }

    if (deliveryAddressFilter) {
      const filterLower = deliveryAddressFilter.toLowerCase();
      filtered = filtered.filter((user) =>
        user.deliveryAddress.toLowerCase().includes(filterLower),
      );
    }

    if (emailModeFilter && emailModeFilter !== "all") {
      filtered = filtered.filter((user) => user.emailMode === emailModeFilter);
    }

    if (smsModeFilter && smsModeFilter !== "all") {
      filtered = filtered.filter((user) => user.smsMode === smsModeFilter);
    }

    return filtered;
  }, [
    usersWithVoicemailNumbers,
    searchTerm,
    deliveryAddressFilter,
    emailModeFilter,
    smsModeFilter,
  ]);

  const handleEdit = useCallback(
    (user) => {
      setSelectedUser(user);
      setEditErrorMessage("");
      setEditFormData(
        {
          firstName: { value: user.firstName, isValid: true },
          lastName: { value: user.lastName, isValid: true },
          userStatus: { value: user.emailMode, isValid: true },
          smsMode: { value: user.smsMode, isValid: true },
          mobileNumber: { value: user.mobileNumber, isValid: true },
          voicemailNumberId: {
            value: user.voicemailNumberId ? String(user.voicemailNumberId) : "",
            isValid: true,
          },
          timeZone: { value: user.timeZone, isValid: true },
        },
        true,
      );
      setShowEditModal(true);
    },
    [setEditFormData],
  );

  const handleEditSubmit = useCallback(async () => {
    if (!selectedUser) return;
    const formData = {
      firstName: editFormState.inputs.firstName.value,
      lastName: editFormState.inputs.lastName.value,
      userStatus: editFormState.inputs.userStatus.value,
      smsMode: editFormState.inputs.smsMode.value,
      mobileNumber: editFormState.inputs.mobileNumber.value,
      voicemailNumberId: editFormState.inputs.voicemailNumberId.value,
      timeZone: editFormState.inputs.timeZone.value,
    };
    const result = await apiUpdateUser(selectedUser.id, formData, selectedUser);
    if (result?.error) {
      setEditErrorMessage(result.error);
      return;
    }

    const nextVoicemailId = formData.voicemailNumberId || "";
    const currentVoicemailId = selectedUser.voicemailNumberId
      ? String(selectedUser.voicemailNumberId)
      : "";

    if (nextVoicemailId !== currentVoicemailId) {
      if (currentVoicemailId) {
        const response = await updatePhoneNumberAssociation(currentVoicemailId, {
          is_associated: false,
          user_id: selectedUser.id,
        });
        if (response?.error) {
          setEditErrorMessage(
            response.error.message || "Unable to unassign voicemail number.",
          );
          return;
        }
      }

      if (nextVoicemailId) {
        const response = await updatePhoneNumberAssociation(nextVoicemailId, {
          is_associated: true,
          user_id: selectedUser.id,
        });
        if (response?.error) {
          setEditErrorMessage(
            response.error.message || "Unable to assign voicemail number.",
          );
          return;
        }
      }

      await loadPhoneNumbers();
      await fetchUsers();
    } else {
      await loadPhoneNumbers();
      await fetchUsers();
    }

    setShowEditModal(false);
    setSelectedUser(null);
    setEditErrorMessage("");
  }, [
    selectedUser,
    editFormState,
    apiUpdateUser,
    fetchUsers,
    loadPhoneNumbers,
    updatePhoneNumberAssociation,
  ]);

  const handleDelete = useCallback(
    (userId) => {
      const user = users.find((u) => u.id === userId);
      setSelectedUser(user);
      setShowDeleteModal(true);
    },
    [users],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedUser) return;
    await apiDeleteUser(selectedUser.id);
    setShowDeleteModal(false);
    setSelectedUser(null);
  }, [selectedUser, apiDeleteUser]);

  const handleAddUser = useCallback(() => {
    setAddFormData(
      {
        firstName: { value: "", isValid: false },
        lastName: { value: "", isValid: false },
        deliveryAddress: { value: "", isValid: false },
        password: { value: "", isValid: false },
        emailMode: { value: "Enabled", isValid: true },
        smsMode: { value: "Disabled", isValid: true },
        mobileNumber: { value: "", isValid: true },
        voicemailNumberId: { value: "", isValid: true },
        timeZone: { value: "UTC", isValid: true },
        userRole: { value: "viewer", isValid: true },
      },
      false,
    );
    setAddErrorMessage("");
    setPendingUserId(null);
    setShowAddModal(true);
  }, [setAddFormData]);

  const handleAddSubmit = useCallback(async () => {
    const formData = {
      firstName: addFormState.inputs.firstName.value,
      lastName: addFormState.inputs.lastName.value,
      deliveryAddress: addFormState.inputs.deliveryAddress.value,
      password: addFormState.inputs.password.value,
      emailMode: addFormState.inputs.emailMode.value,
      smsMode: addFormState.inputs.smsMode.value,
      mobileNumber: addFormState.inputs.mobileNumber.value,
      voicemailNumberId: addFormState.inputs.voicemailNumberId.value,
      timeZone: addFormState.inputs.timeZone.value,
      userRole: addFormState.inputs.userRole.value,
    };

    if (pendingUserId) {
      if (!formData.voicemailNumberId) {
        setAddErrorMessage("Select a voicemail number to finish association.");
        return;
      }

      const response = await updatePhoneNumberAssociation(
        formData.voicemailNumberId,
        {
          is_associated: true,
          user_id: pendingUserId,
        },
      );

      if (response?.error) {
        setAddErrorMessage(
          response.error.message || "Unable to assign voicemail number.",
        );
        return;
      }

      await loadPhoneNumbers();
      await fetchUsers();
      setPendingUserId(null);
      setShowAddModal(false);
      setAddErrorMessage("");
      return;
    }

    const result = await apiAddUser(formData);
    if (result?.error) {
      setAddErrorMessage(result.error);
      return;
    }

    if (formData.voicemailNumberId && result?.userId) {
      const response = await updatePhoneNumberAssociation(
        formData.voicemailNumberId,
        {
          is_associated: true,
          user_id: result.userId,
        },
      );

      if (response?.error) {
        setPendingUserId(result.userId);
        setAddErrorMessage(
          response.error.message ||
            "User created, but voicemail association failed. Please retry.",
        );
        return;
      }
    }

    await loadPhoneNumbers();
    await fetchUsers();
    setShowAddModal(false);
    setAddErrorMessage("");
  }, [
    addFormState,
    apiAddUser,
    fetchUsers,
    loadPhoneNumbers,
    pendingUserId,
    updatePhoneNumberAssociation,
  ]);

  const handleBulkImport = useCallback(() => {
    setShowBulkImportModal(true);
  }, []);

  const handleBulkImportSubmit = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n").filter((line) => line.trim());
        if (lines.length < 2) {
          alert("CSV file is empty or invalid");
          return;
        }
        alert(`CSV parsed: ${lines.length - 1} row(s) found. Bulk API import not yet supported.`);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        alert("Error parsing CSV file. Please check the format.");
      } finally {
        setShowBulkImportModal(false);
      }
    };
    reader.onerror = () => {
      alert("Error reading file");
      setShowBulkImportModal(false);
    };
    reader.readAsText(file);
  }, []);

  const handleCancel = useCallback(() => {
    setShowEditModal(false);
    setShowAddModal(false);
    setShowDeleteModal(false);
    setShowBulkDeleteModal(false);
    setShowBulkImportModal(false);
    setSelectedUser(null);
    setAddErrorMessage("");
    setEditErrorMessage("");
    setPendingUserId(null);
  }, []);

  const handleBulkEnable = useCallback(async () => {
    const ids = Array.from(selectedRows);
    if (ids.length === 0) return;
    await apiBulkStatusChange(ids, "enabled");
    setSelectedRows(new Set());
  }, [selectedRows, apiBulkStatusChange]);

  const handleBulkDisable = useCallback(async () => {
    const ids = Array.from(selectedRows);
    if (ids.length === 0) return;
    await apiBulkStatusChange(ids, "disabled");
    setSelectedRows(new Set());
  }, [selectedRows, apiBulkStatusChange]);

  const handleBulkDelete = useCallback(() => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  }, [selectedRows]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    const ids = Array.from(selectedRows);
    await apiBulkDelete(ids);
    setSelectedRows(new Set());
    setShowBulkDeleteModal(false);
  }, [selectedRows, apiBulkDelete]);

  const handleFilter = () => {};

  const handlePrint = () => {
    const tableArea = document.querySelector(".logs-table-area");
    if (!tableArea) return;

    const table = tableArea.querySelector("table");
    if (!table) return;

    const tableClone = table.cloneNode(true);
    const ths = tableClone.querySelectorAll("th");
    ths.forEach((th) => {
      th
        .querySelectorAll("svg, i, span[class*=icon], .fa, .svg-inline--fa")
        .forEach((el) => el.remove());
    });

    const printWindow = window.open("", "", "width=900,height=700");
    if (!printWindow) return;

    printWindow.document.write("<html><head><title>Print Table</title>");
    printWindow.document.write(
      "<style>body{font-family:sans-serif;padding:16px;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ccc;padding:8px;text-align:left;}th input,td input,button{display:none!important;}</style>",
    );
    printWindow.document.write("</head><body>");
    printWindow.document.write(
      "<table>" +
        (tableClone.tHead ? tableClone.tHead.outerHTML : "") +
        (tableClone.tBodies[0] ? tableClone.tBodies[0].outerHTML : "") +
        "</table>",
    );
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleDownload = useCallback(() => {
    if (filteredUsers.length === 0) return;
    const headers = [
      "First Name", "Last Name", "To Address (PBX Email)",
      "Delivery Address (Email)", "Email Mode", "SMS Mode",
      "Mobile Number", "Voicemail Number", "Time Zone",
    ];
    const rows = filteredUsers.map((u) => [
      u.firstName, u.lastName, u.toAddress, u.deliveryAddress,
      u.emailMode, u.smsMode, u.mobileNumber, u.voicemailNumber, u.timeZone,
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${(cell ?? "").toString().replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users.csv";
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredUsers]);

  const handleSpreadTableToggle = useCallback(() => {
    setSpreadTable((prev) => !prev);
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <input
            type="checkbox"
            checked={
              selectedRows.size === filteredUsers.length &&
              filteredUsers.length > 0
            }
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRows(new Set(filteredUsers.map((u) => u.id)));
              } else {
                setSelectedRows(new Set());
              }
            }}
            className="w-4 h-4 text-primary border-gray-300 rounded-sm focus:ring-primary !bg-primary"
          />
        ),
        accessor: "checkbox",
        disableSortBy: true,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRows.has(row.original.id)}
            onChange={(e) => {
              const newSelected = new Set(selectedRows);
              if (e.target.checked) {
                newSelected.add(row.original.id);
              } else {
                newSelected.delete(row.original.id);
              }
              setSelectedRows(newSelected);
            }}
            className="w-4 h-4 text-primary border-gray-300 rounded-sm focus:ring-primary"
          />
        ),
      },
      {
        Header: "FIRST NAME",
        accessor: "firstName",
        Cell: ({ value }) => (
          <span className="text-content text-xs sm:text-sm">{value}</span>
        ),
      },
      {
        Header: "LAST NAME",
        accessor: "lastName",
        Cell: ({ value }) => (
          <span className="text-content text-xs sm:text-sm">{value}</span>
        ),
      },
      {
        Header: () => (
          <div className="flex items-center gap-1">
            <span>TO ADDRESS</span>
            <Tooltip text="Email address where PBX voicemails are sent to for transcription.">
              <InfoIcon className="text-xs text-gray-500 cursor-help" />
            </Tooltip>
          </div>
        ),
        accessor: "toAddress",
        Cell: ({ value }) => (
          <span className="text-content text-xs sm:text-sm">{value || "—"}</span>
        ),
      },
      {
        Header: () => (
          <div className="flex items-center gap-1">
            <span>DELIVERY ADDRESS</span>
            <Tooltip text="End user email address to deliver transcribed messages to">
              <InfoIcon className="text-[10px] text-content cursor-help" />
            </Tooltip>
          </div>
        ),
        accessor: "deliveryAddress",
        Cell: ({ value }) => (
          <span className="text-content text-xs sm:text-sm">{value}</span>
        ),
      },
      {
        Header: "EMAIL MODE",
        accessor: "emailMode",
        disableSortBy: true,
        Cell: ({ value }) => <StatusBadge status={value} variant="email" />,
      },
      {
        Header: "SMS MODE",
        accessor: "smsMode",
        disableSortBy: true,
        Cell: ({ value }) => <StatusBadge status={value} variant="sms" />,
      },
      {
        Header: "MOBILE NUMBER",
        accessor: "mobileNumber",
        Cell: ({ value }) => (
          <span className="text-content text-xs sm:text-sm">{value || "—"}</span>
        ),
      },
      {
        Header: "VOICEMAIL NUMBER",
        accessor: "voicemailNumber",
        Cell: ({ value }) => (
          <span className="text-content text-xs sm:text-sm">{value || "—"}</span>
        ),
      },
      {
        Header: "TIME ZONE",
        accessor: "timeZone",
        Cell: ({ value }) => (
          <span className="text-content text-xs sm:text-sm">{value}</span>
        ),
      },
      {
        Header: "ACTIONS",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <ActionButtons
              onEdit={() => handleEdit(row.original)}
              onDelete={() => handleDelete(row.original.id)}
              userEmail={row.original.deliveryAddress || row.original.toAddress || ""}
              currentUserEmail={currentUserEmail}
            />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRows, filteredUsers, handleEdit, handleDelete],
  );

  const emailModeOptions = [
    { name: "All Email Modes", value: "all" },
    { name: "Enabled", value: "Enabled" },
    { name: "Disabled", value: "Disabled" },
  ];

  const smsModeOptions = [
    { name: "All SMS Modes", value: "all" },
    { name: "Enabled", value: "Enabled" },
    { name: "Disabled", value: "Disabled" },
  ];

  return (
    <div className="flex flex-col w-full h-full bg-transparent">
      <UserManagementHeader
        onBulkImport={handleBulkImport}
        onAddUser={handleAddUser}
        useMUIcons={true}
      />

      <div
        className={`sm:mx-2 border border-line rounded-t-xl shadow-sm flex flex-col flex-1 logs-table-area ${
          spreadTable ? " table-zoom-out" : ""
        }`}
      >
        <div className="p-4 bg-white border-b border-line flex max-[400px]:flex-col flex-row justify-between sm:items-center rounded-t-xl max-[400px]:gap-4 gap-2 sm:gap-0">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search users"
            useMUIicons={false}
          />

          <TableToolbar
            useMUIicons={false}
            spreadTable={spreadTable}
            onSpreadTableToggle={handleSpreadTableToggle}
            onFilter={handleFilter}
            onPrint={handlePrint}
            onDownload={handleDownload}
            displayData={filteredUsers}
            columns={columns}
            deliveryAddressFilter={deliveryAddressFilter}
            setDeliveryAddressFilter={setDeliveryAddressFilter}
            emailModeFilter={emailModeFilter}
            setEmailModeFilter={setEmailModeFilter}
            smsModeFilter={smsModeFilter}
            setSmsModeFilter={setSmsModeFilter}
            emailModeOptions={emailModeOptions}
            smsModeOptions={smsModeOptions}
          />
        </div>

        <SelectionToolbar
          selectedCount={selectedRows.size}
          onEnable={handleBulkEnable}
          onDisable={handleBulkDisable}
          onDelete={handleBulkDelete}
        />

        <div className="flex-1 overflow-auto">
          <Table
            columns={columns}
            data={filteredUsers}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            selectedRows={selectedRows}
            pageSize={10}
            showTotalRecords={true}
            totalRecords={filteredUsers.length}
            showPageSizeSelector={true}
            useMUIcons={false}
            rowIdKey="id"
            loading={isLoading || isProcessing}
          />
        </div>
      </div>

      <EditUserModal
        show={showEditModal}
        formState={editFormState}
        inputHandler={editInputHandler}
        selectHandler={editSelectHandler}
        onCancel={handleCancel}
        onSubmit={handleEditSubmit}
        isProcessing={isProcessing}
        errorMessage={editErrorMessage}
        voicemailNumbers={editVoicemailNumbers}
        selectedVoicemailLabel={selectedVoicemailLabel}
      />

      <AddUserModal
        show={showAddModal}
        formState={addFormState}
        inputHandler={addInputHandler}
        selectHandler={addSelectHandler}
        onCancel={handleCancel}
        onSubmit={handleAddSubmit}
        isProcessing={isProcessing}
        errorMessage={addErrorMessage}
        voicemailNumbers={availableVoicemailNumbers}
      />

      <DeleteUserModal
        show={showDeleteModal}
        onCancel={handleCancel}
        onConfirm={handleDeleteConfirm}
      />

      <DeleteUserModal
        show={showBulkDeleteModal}
        voicemailNumbers={availableVoicemailNumbers}
        onCancel={handleCancel}
        onConfirm={handleBulkDeleteConfirm}
        count={selectedRows.size}
      />

      <BulkImportModal
        show={showBulkImportModal}
        onCancel={handleCancel}
        onImport={handleBulkImportSubmit}
      />
    </div>
  );
};

export default UserManagement;
